import { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./DatePickerInput.css";
import { Calendar } from "lucide-react";
import { parse, format } from "date-fns";
import { enGB } from "date-fns/locale"; // day-first locale

interface DatePickerInputProps {
  value?: string; // supports "dd/MM/yyyy" or "yyyy-MM-dd"
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  maxDate?: Date;
  className?: string;
  scrollContainerSelector?: string;
}

const DatePickerInput: React.FC<DatePickerInputProps> = ({
  value = "",
  onChange,
  error = "",
  placeholder = "Select date",
  maxDate = new Date(),
  scrollContainerSelector,
  className = "",
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const datepickerRef = useRef<DatePicker>(null);

  // Parse incoming `value` safely with an explicit format
  useEffect(() => {
    if (!value) {
      setSelectedDate(null);
      return;
    }
    let parsed: Date | null = null;

    // Support both dd/MM/yyyy and ISO yyyy-MM-dd from parent
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      parsed = parse(value, "dd/MM/yyyy", new Date());
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      parsed = parse(value, "yyyy-MM-dd", new Date());
    } else {
      // last-resort: try Date constructor (not recommended)
      const d = new Date(value);
      parsed = isNaN(d.getTime()) ? null : d;
    }

    setSelectedDate(parsed);
  }, [value]);

  // Close on outer scroll (unchanged)
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    const scrollTarget = scrollContainerSelector
      ? document.querySelector(scrollContainerSelector)
      : window;

    scrollTarget?.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      scrollTarget?.removeEventListener("scroll", handleScroll);
    };
  }, [isOpen, scrollContainerSelector]);

  return (
    <div className={`relative w-full`}>
      {/* Click-through overlay to focus the input */}
      <div
        className="absolute inset-0 z-10 cursor-pointer"
        onClick={() => datepickerRef.current?.setFocus()}
      />

      <div className="relative">
        <DatePicker
          ref={datepickerRef}
          selected={selectedDate}
          onChange={(date: Date | null) => {
            setSelectedDate(date);
            // 🔒 Always emit dd/MM/yyyy to parent
            onChange(date ? format(date, "dd/MM/yyyy") : "");
            setIsOpen(false);
          }}
          onCalendarOpen={() => setIsOpen(true)}
          onCalendarClose={() => setIsOpen(false)}
          open={isOpen}
          onClickOutside={() => setIsOpen(false)}
          dateFormat="dd/MM/yyyy"
          placeholderText={placeholder}
          maxDate={maxDate}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          portalId="root"
          locale={enGB} // ✅ day-first calendar behavior
          onKeyDown={e => e.preventDefault()}
          className={`relative z-0 w-full rounded-md border ${className} ${
            error ? "border-red-500" : "border-[#DFE7EC]"
          } bg-white px-10 py-2.5 font-nunito text-[16px] font-semibold text-[#2F363F] placeholder-[#BECAD0] outline-none focus:border-[#1DAB91]`}
        />

        <Calendar
          size={20}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#707070]"
        />
      </div>

      {error && (
        <p className="mt-1 font-nunito text-sm font-semibold text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};

export default DatePickerInput;
